import axios from 'axios';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { resetAuth } from '../store/slices/auth-slice';
import { clearAuthCookies } from '../utils/cookies';
import { ExceptionEnum, extractExceptionCode } from '../utils/exception';

type RequestFn<T> = () => Promise<T>;

export type UseRequestResult<T> = ((
  request: RequestFn<T>,
) => Promise<T | null>) & {
  isLoading: boolean;
  isDone: boolean;
  isError: boolean;
  data: T | null;
  exception: ExceptionEnum | null;
  fetch: (request: RequestFn<T>) => Promise<T | null>;
};

export function useRequest<T = unknown>(): UseRequestResult<T> {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState<T | null>(null);
  const [exception, setException] = useState<ExceptionEnum | null>(null);

  const fetch = useCallback(
    async (request: RequestFn<T>): Promise<T | null> => {
      setIsLoading(true);
      setIsDone(false);
      setIsError(false);
      setException(null);

      try {
        const result = await request();
        setData(result);
        setIsDone(true);
        return result;
      } catch (error) {
        setIsError(true);
        setIsDone(true);
        setException(extractExceptionCode(error));

        if (axios.isAxiosError(error) && error.response?.status === 401) {
          clearAuthCookies();
          dispatch(resetAuth());
          navigate('/login', { replace: true });
        }

        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, navigate],
  );

  // Stable identity (same function) across renders so hooks can depend on the
  // request object without re-firing when isLoading/data/exception change.
  return Object.assign(fetch, {
    isLoading,
    isDone,
    isError,
    data,
    exception,
    fetch,
  }) as UseRequestResult<T>;
}
